import { useEffect, useRef, useState } from "react";
import moment from "moment/moment";
import { FaCheck, FaCircleInfo, FaXmark } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useMutation, useQuery } from "@tanstack/react-query";

import * as images from "../../assets/image";
import {
  deleteEvent,
  getEventWithPayouts,
  syncEvents,
  updateEventStatus
} from "../../services/services";
import { constant } from "../../utils/constants";
import Pagination from "../../utils/Pagination";
import { toastAlert } from "../../utils/SweetAlert";

const Shifts = () => {
  const allowedTimesheetStatus = ["accepted", "confirmed"];
  const [search, setSearch] = useState();
  const navigate = useNavigate();
  const [isToggled, setIsToggled] = useState(false);
  const [page, setPage] = useState(constant.PAGE_NO_ONE);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const handleToggle = () => {
    setIsToggled(!isToggled);
  };

  const { data, refetch } = useQuery({
    queryKey: ["shift-list", page, isToggled],
    queryFn: async () => {
      let params = {
        page: page,
        sortedBy: constant.SORTED_BY.START_TIME,
      };
      if (isToggled) {
        (params.previousEvents = true), (params.sortBy = constant.SORT_BY.DESC);
      } else {
        (params.futureEvents = true), (params.sortBy = constant.SORT_BY.ASC);
      }
      if (search) params.search = search;

      const resp = await getEventWithPayouts(params);
      return resp?.data?.data || {};
    },
  });

  const handleEventSync = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to sync events to your Google calendar",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, sync it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          syncEventsMutation.mutate();
        } catch (err) {
          console.log("err", err);
        }
      }
    });
  };

  const syncEventsMutation = useMutation({
    mutationFn: () => syncEvents(),
    onSuccess: async (resp) => {
      if (resp.data.statusCode == constant.STATUS_CODE.SUCCESS) {
        toastAlert("success", resp.data?.message);
      }
      refetch();
    },
  });

  const eventMutation = useMutation({
    mutationFn: ({ id, status }) => updateEventStatus(id, status),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      refetch();
    },
  });

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteEvent(id),
    onSuccess: (resp) => {
      Swal.fire({
        title: "Deleted!",
        text: resp?.data?.message,
        icon: "success",
        confirmButtonColor: "#0d1227",
      });
      refetch();
    },
  });

  const toggleEventSelection = (eventId) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  const toggleSelectAll = () => {
    const selectableEvents = data?.events?.filter(
      (event) =>
        event.status !== "confirmed" &&
        event.status !== "accepted" &&
        event.status !== "rejected" &&
        event.status !== "cancelled"
    ) || [];

    if (selectedEvents.length === selectableEvents.length) {
      setSelectedEvents([]);
    } else if (selectableEvents.length > 0) {
      setSelectedEvents(selectableEvents.map((event) => event._id));
    }
  };

  const approveSelectedEvents = async () => {
    if (selectedEvents.length === 0) return;

    setSelectedEvents([]);
    await Promise.all(
      selectedEvents.map((id) =>
        eventMutation.mutateAsync({ id, status: constant.EVENT_STATUS.ACCEPTED })
      )
    );
    refetch();
  };

  const denySelectedEvents = async () => {
    if (selectedEvents.length === 0) return;

    Swal.fire({
      title: "Are you sure?",
      text: "Do you wish to deny the selected assignment(s)?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, deny it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSelectedEvents([]);
        await Promise.all(
          selectedEvents.map((id) =>
            eventMutation.mutateAsync({ id, status: constant.EVENT_STATUS.REJECTED })
          )
        );
        refetch();
      }
    });
  };

  const isEventTodayOrTomorrow = (startTime) => {
    const eventStartTime = moment(startTime);
    return (
      eventStartTime.isSame(moment(), "day") ||
      eventStartTime.isSame(moment().add(1, "day"), "day")
    );
  };

  const clickTimer = useRef(null);
  const handleEventClick1 = () => {
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      Swal.fire({
        icon: "warning",
        title: "Actions",
        text: "Please contact manager to change schedule!",
        showCloseButton: false,
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
    }, 300);
  };

  const handleTimesheet = (event) => {
    const { _id, clientDetails, startTime, endTime, payoutDetails } = event;
    if (payoutDetails) return;
    const eventStartTime = moment(startTime);
    const eventEndTime = moment(endTime);
    const labelValue = `${eventStartTime.format("ll")} (${eventStartTime.format(
      constant.HOURS_FORMAT
    )} - ${eventEndTime.format(constant.HOURS_FORMAT)})`;
    const navigateData = {
      clientId: {
        label: `${clientDetails?.firstName} ${clientDetails?.lastName}`,
        value: clientDetails._id,
      },
      eventId: { label: labelValue, value: _id },
      checkIn: new Date(startTime),
      checkOut: new Date(endTime),
      date: new Date(startTime),
    };
    navigate("/employee/add-timesheet", {
      state: { formValues: navigateData },
    });
  };

  const handleClientNote = (clientDetails) => {
    if (!clientDetails?.note?.trim()) return;

    Swal.fire({
      title: `${clientDetails?.firstName || "Client"}'s Note`,
      text: clientDetails.note,
      icon: "info",
      confirmButtonText: "Okay",
      confirmButtonColor: "#3085d6",
    });
  };

  const tableContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      console.log("isMobile:", window.innerWidth < 768, "Window Width:", window.innerWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className="main-content schedules_bar">
      <div className="commonSearchBar mt-3">
        <h4 className="heading20">Upcoming Schedules</h4>
        <div className="endContent">
          <div className="toggle-container">
            <div
              className={`toggle-switch ${isToggled ? "toggled" : ""}`}
              onClick={handleToggle}
            >
              <div className={`slider ${isToggled ? "toggled" : ""}`}></div>
            </div>
            <label className="label-text">Past Assignments</label>
          </div>
          <div className="clientSearch">
            <img
              src={images.Search}
              alt="searchImg"
              className="clientSearchIcon"
            />
            <input
              type="text"
              placeholder="Type and press enter to search."
              className="clientSearchInput"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onKeyDown={(e) =>
                e.key === "Enter" && search?.trim() !== "" && refetch()
              }
              onKeyUp={(e) =>
                e.target.value === "" && e.key === "Backspace" && refetch()
              }
            />
          </div>
        </div>
      </div>

      <div className="container-fluid p-0">
        <div className="row mt-4">
          <div className="col-md-12">
            <div style={{ marginBottom: "15px" }}>
              <button
                style={{
                  backgroundColor: "#0d1227",
                  color: "white",
                  border: "none",
                  padding: "8px 15px",
                  borderRadius: "4px",
                  marginRight: "10px",
                  opacity: selectedEvents.length === 0 || eventMutation.isLoading ? 0.5 : 1,
                }}
                onClick={approveSelectedEvents}
                disabled={selectedEvents.length === 0 || eventMutation.isLoading}
              >
                Accept Selected
              </button>
              <button
                style={{
                  backgroundColor: "#0d1227",
                  color: "white",
                  border: "none",
                  padding: "8px 15px",
                  borderRadius: "4px",
                  opacity: selectedEvents.length === 0 || eventMutation.isLoading ? 0.5 : 1,
                }}
                onClick={denySelectedEvents}
                disabled={selectedEvents.length === 0 || eventMutation.isLoading}
              >
                Deny Selected
              </button>
            </div>

            <div
              ref={tableContainerRef}
              style={{
                overflowX: isMobile ? "auto" : "visible",
                WebkitOverflowScrolling: "touch",
                width: "100%",
                position: "relative",
                paddingBottom: isMobile ? "20px" : "0",
              }}
            >
              {isMobile && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#666",
                    fontSize: "12px",
                  }}
                >
                  ← Scroll →
                </div>
              )}

              <table
                style={{
                  width: "100%",
                  minWidth: isMobile ? "800px" : "100%",
                  borderCollapse: "separate",
                  borderSpacing: "0",
                }}
              >
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th
                      className="tableHeading"
                      style={{
                        width: isMobile ? "30px" : "40px",
                        minWidth: isMobile ? "30px" : "40px",
                        borderRadius: "10px 0px 0px",
                      }}
                    >
                      <input
                        type="checkbox"
                        onChange={toggleSelectAll}
                        checked={
                          data?.events?.length > 0 &&
                          data?.events?.some(
                            (event) =>
                              event.status !== "confirmed" &&
                              event.status !== "accepted" &&
                              event.status !== "rejected" &&
                              event.status !== "cancelled"
                          ) &&
                          selectedEvents.length ===
                          data?.events?.filter(
                            (event) =>
                              event.status !== "confirmed" &&
                              event.status !== "accepted" &&
                              event.status !== "rejected" &&
                              event.status !== "cancelled"
                          ).length
                        }
                      />
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? "0" : "auto",
                        zIndex: isMobile ? "4" : "auto",
                        boxShadow: isMobile
                          ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                          : "none",
                        minWidth: isMobile ? "80px" : "100px",
                      }}
                    >
                      Date
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? "78px" : "auto",
                        zIndex: isMobile ? "4" : "auto",
                        boxShadow: isMobile
                          ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                          : "none",
                        minWidth: isMobile ? "100px" : "120px",
                      }}
                    >
                      Time
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? "158px" : "auto",
                        zIndex: isMobile ? "4" : "auto",
                        boxShadow: isMobile
                          ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                          : "none",
                        minWidth: isMobile ? "70px" : "120px",
                      }}
                    >
                      {isMobile ? (
                        <>
                          <div>Client</div>
                          <div>Name</div>
                        </>
                      ) : (
                        "Client Name"
                      )}
                    </th>
                    <th
                      className="tableHeading"
                      style={{ display: isMobile ? "none" : "table-cell" }}
                    >
                      Note
                    </th>
                    <th
                      className="tableHeading"
                      style={{ minWidth: isMobile ? "120px" : "150px" }}
                    >
                      Address
                    </th>
                    <th
                      className="tableHeading"
                      style={{ minWidth: isMobile ? "80px" : "100px" }}
                    >
                      Phone
                    </th>
                    <th
                      className="tableHeading"
                      style={{ display: isMobile ? "none" : "table-cell" }}
                    >
                      Email
                    </th>
                    <th className="tableHeading" style={{ minWidth: "80px" }}>
                      Accept/Deny
                    </th>
                    <th className="tableHeading" style={{ minWidth: "80px" }}>
                      Status
                    </th>
                    <th className="tableHeading" style={{ minWidth: "80px" }}>
                      Confirm
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "90px",
                        borderRadius: "0px 10px 0px 0px",
                      }}
                    >
                      Timesheet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data && data?.events && data?.events?.length > 0 ? (
                    data?.events?.map((event, index) => {
                      const clientDetails = event?.clientDetails || null;
                      const note = event?.note?.trim() || "N/A";
                      const shortNote =
                        note.length > 20 ? `${note.slice(0, 20)}...` : note;
                      return (
                        <tr key={`event_${index}`}>
                          <td
                            style={{
                              width: isMobile ? "30px" : "40px",
                              minWidth: isMobile ? "30px" : "40px",
                            }}
                          >
                            {event?.status !== "confirmed" &&
                              event?.status !== "cancelled" &&
                              event?.status !== "accepted" &&
                              event?.status !== "rejected" && (
                                <input
                                  type="checkbox"
                                  checked={selectedEvents.includes(event._id)}
                                  onChange={() =>
                                    toggleEventSelection(event._id)
                                  }
                                />
                              )}
                          </td>
                          <td
                            style={{
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? "0" : "auto",
                              zIndex: isMobile ? "4" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                              boxShadow: isMobile
                                ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                                : "none",
                              minWidth: isMobile ? "80px" : "100px",
                            }}
                          >
                            {moment(event?.startTime).format(
                              "ddd, MMM DD, YYYY"
                            )}
                          </td>
                          <td
                            style={{
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? "78px" : "auto",
                              zIndex: isMobile ? "4" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                              boxShadow: isMobile
                                ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                                : "none",
                              minWidth: isMobile ? "100px" : "120px",
                            }}
                          >
                            {moment(event?.startTime).format("LT")} -{" "}
                            {moment(event?.endTime).format("LT")}
                          </td>
                          <td
                            style={{
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? "158px" : "auto",
                              zIndex: isMobile ? "4" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                              boxShadow: isMobile
                                ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                                : "none",
                              minWidth: isMobile ? "70px" : "120px",
                              textOverflow: isMobile ? "initial" : "ellipsis",
                              whiteSpace: isMobile ? "normal" : "nowrap",
                              overflow: isMobile ? "visible" : "hidden",
                              wordBreak: isMobile ? "break-word" : "normal",
                            }}
                            className="text-capitalize"
                            title={
                              isMobile
                                ? ""
                                : `${clientDetails?.firstName} ${clientDetails?.lastName}`
                            }
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: "8px",
                              }}
                            >
                              {isMobile ? (
                                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                                  <div>{clientDetails?.firstName}</div>
                                  <div>{clientDetails?.lastName}</div>
                                </div>
                              ) : (
                                <span style={{ flex: 1, minWidth: 0 }}>{`${clientDetails?.firstName} ${clientDetails?.lastName}`}</span>
                              )}
                              {clientDetails?.note?.trim() && (
                                <button
                                  type="button"
                                  title="View note"
                                  aria-label="View client note"
                                  onClick={() => handleClientNote(clientDetails)}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    margin: 0,
                                    color: "#0d1227",
                                    cursor: "pointer",
                                    lineHeight: 0,
                                    flexShrink: 0,
                                  }}
                                >
                                  <FaCircleInfo size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td
                            style={{
                              display: isMobile ? "none" : "table-cell",
                            }}
                            title={note}
                          >
                            {shortNote}
                          </td>
                          <td>{`${clientDetails?.userAddress?.address} ${clientDetails?.userAddress?.city
                            } ${clientDetails?.userAddress?.state} ${clientDetails?.userAddress?.country || ""
                            } (${clientDetails?.userAddress?.zipCode})`}</td>
                          <td>{clientDetails?.phoneNumber}</td>
                          <td
                            style={{
                              display: isMobile ? "none" : "table-cell",
                            }}
                          >
                            {clientDetails?.email}
                          </td>
                          <td>
                            <FaCheck
                              size={20}
                              role="button"
                              className="me-2"
                              onClick={() => {
                                if (
                                  event.status !== constant.EVENT_STATUS.ACCEPTED &&
                                  event.status !== "confirmed" &&
                                  event.status !== "accepted" &&
                                  event.status !== "rejected"
                                ) {
                                  eventMutation.mutate({
                                    id: event?._id,
                                    status: constant.EVENT_STATUS.ACCEPTED,
                                  });
                                } else {
                                  handleEventClick1();
                                }
                              }}
                            />
                            <FaXmark
                              size={20}
                              disabled={event?.status === "confirmed"}
                              role="button"
                              onClick={() => {
                                if (
                                  event.status !==
                                  constant.EVENT_STATUS.REJECTED &&
                                  event.status !== "confirmed" &&
                                  event.status !== "accepted"
                                ) {
                                  Swal.fire({
                                    title: "Are you sure?",
                                    text: "Do you wish to deny the selected assignment(s)?",
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonColor: "#d33",
                                    cancelButtonColor: "#6c757d",
                                    confirmButtonText: "Yes, deny it!",
                                    cancelButtonText: "Cancel",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      eventMutation.mutate({
                                        id: event?._id,
                                        status: constant.EVENT_STATUS.REJECTED,
                                      });
                                    }
                                  });
                                } else {
                                  handleEventClick1();
                                }
                              }}
                            />
                          </td>
                          <td>
                            {event?.status === "accepted" ? (
                              <span className="text-success">Accepted</span>
                            ) : event?.status === "rejected" ? (
                              <span className="text-danger">Denied</span>
                            ) : event?.status === "confirmed" ? (
                              <span className="text-primary">Confirmed</span>
                            ) : event?.status === "cancelled" ? (
                              <span className="text-primary">Cancelled</span>
                            ) : (
                              <span className="text-warning">Pending</span>
                            )}
                          </td>
                          <td>
                            {(event?.status === "accepted" ||
                              event?.status === "confirmed") &&
                              isEventTodayOrTomorrow(event?.startTime) && (
                                <button
                                  style={{
                                    backgroundColor: "green",
                                    color: "white",
                                    border: "none",
                                    padding: "5px 10px",
                                    borderRadius: "4px",
                                  }}
                                  disabled={event?.status === "confirmed"}
                                  onClick={() =>
                                    eventMutation.mutate({
                                      id: event?._id,
                                      status: constant.EVENT_STATUS.CONFIRMED,
                                    })
                                  }
                                >
                                  {event?.status !== "confirmed"
                                    ? "Confirm"
                                    : "Confirmed"}
                                </button>
                              )}
                          </td>
                          <td>
                            {moment(event?.startTime).isBefore() &&
                              allowedTimesheetStatus.includes(event?.status) ? (
                              <button
                                style={{
                                  backgroundColor: "#0d1326",
                                  color: "white",
                                  border: "none",
                                  padding: "5px 10px",
                                  borderRadius: "4px",
                                }}
                                type="button"
                                title="Timesheet"
                                onClick={() => handleTimesheet(event)}
                              >
                                {event.payoutDetails ? "Signed" : "TimeSheet"}
                              </button>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td style={{ textAlign: "center" }} colSpan={12}>
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: "15px" }}>
              <button
                style={{
                  backgroundColor: "#0d1227",
                  color: "white",
                  border: "none",
                  padding: "8px 15px",
                  borderRadius: "4px",
                  marginRight: "10px",
                  opacity: selectedEvents.length === 0 || eventMutation.isLoading ? 0.5 : 1,
                }}
                onClick={approveSelectedEvents}
                disabled={selectedEvents.length === 0 || eventMutation.isLoading}
              >
                Accept Selected
              </button>
              <button
                style={{
                  backgroundColor: "#0d1227",
                  color: "white",
                  border: "none",
                  padding: "8px 15px",
                  borderRadius: "4px",
                  opacity: selectedEvents.length === 0 || eventMutation.isLoading ? 0.5 : 1,
                }}
                onClick={denySelectedEvents}
                disabled={selectedEvents.length === 0 || eventMutation.isLoading}
              >
                Deny Selected
              </button>
            </div>

            <Pagination
              currentPage={page}
              totalPages={data?.total_pages}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Shifts;
